'use client';

import React, { useState } from 'react';
import { PlayIcon } from '@heroicons/react/20/solid';
import { Transition } from '@headlessui/react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { buildTrackingId } from '@/utils/tracking-helper';
import Button from '../../shared/Button';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { VideoTile } from './types/VideoTile';

// Importing dynamicly fixed the Hydration Mismatch error
const ReactPlayer = dynamic(() => import('react-player'));

export default function Video({
  tile,
  trackingPrefix,
}: Readonly<{ tile: VideoTile; trackingPrefix?: string }>) {
  const t = useTranslations('Video');
  const [playing, setPlaying] = useState(false);
  const video = tile.video?.videos?.[0];
  const youtubeLink = tile['youtube-link']?.text;
  const { title } = tile;
  const { subtitle } = tile;
  const playTrackingId = buildTrackingId(trackingPrefix, 'play');

  if (video) {
    const videoThumbnail = video.thumbnails?.[0];

    return (
      <Container>
        <div className=" relative overflow-hidden rounded-xl">
          <Transition
            show={!playing}
            leave="transition-opacity duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <OptimizedImage image={videoThumbnail} fill className="object-cover" sizes="100vw" />
            {/* Gradient overlay */}
            <div className="absolute inset-0 -z-0 bg-gradient-middle" />
            <div className="absolute left-1/2 top-1/2 z-10 flex max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 text-center text-white sm:gap-4">
              <RichTextRenderer
                blocks={title}
                className="text-xl sm:text-3xl md:text-4xl lg:text-5xl"
              />
              <RichTextRenderer
                blocks={subtitle}
                className="text sm:text-md md:text-lg lg:text-2xl"
              />
              <Button
                onClick={() => setPlaying(true)}
                className="flex w-fit items-center gap-1 text-neutral-100"
                aria-label={tile['button-text']?.text || t('playVideo')}
                data-tile-element-id={playTrackingId}
              >
                <PlayIcon className="h-4 w-4" aria-hidden="true" />
                {tile['button-text']?.text}
              </Button>
            </div>
          </Transition>
          <ReactPlayer
            url={video.playlists?.[1]}
            width="100%"
            height="100%"
            playing={playing}
            controls={playing}
            playsinline
          />
        </div>
      </Container>
    );
  }

  if (youtubeLink) {
    return (
      <Container>
        <div className="relative overflow-hidden rounded-xl pb-[56.25%]">
          <Transition
            show={!playing}
            leave="transition-opacity duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <OptimizedImage
              imageComponent={tile['youtube-thumbnail']}
              fill
              className="z-10 object-cover"
              sizes="100vw"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 z-20 bg-gradient-middle" />
            <div className="absolute left-1/2 top-1/2 z-30 flex max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-4 text-center text-white">
              <RichTextRenderer
                blocks={title}
                className="text-xl sm:text-3xl md:text-4xl lg:text-5xl"
              />
              <RichTextRenderer
                blocks={subtitle}
                className="text sm:text-md md:text-lg lg:text-2xl"
              />
              <Button
                onClick={() => setPlaying(true)}
                className="flex w-fit items-center gap-1 text-neutral-100"
                aria-label={tile['button-text']?.text || t('playVideo')}
                data-tile-element-id={playTrackingId}
              >
                <PlayIcon className="h-4 w-4" aria-hidden="true" />
                {tile['button-text']?.text}
              </Button>
            </div>
          </Transition>
          <ReactPlayer
            url={youtubeLink}
            width="100%"
            height="100%"
            className="absolute left-0 top-0 h-full w-full"
            playing={playing}
            controls
            playsinline
          />
        </div>
      </Container>
    );
  }
}
